import { useCallback, useEffect, useRef } from 'react';

/** Default delay (ms) for search input debouncing. */
export const SEARCH_DEBOUNCE_MS = 400;

/**
 * Returns a debounced version of the callback that delays execution until after `delay` ms
 * have elapsed since the last call. Cleans up pending timeout on unmount.
 */
export function useDebouncedCallback<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  const fnRef = useRef(fn);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  fnRef.current = fn;

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        (fnRef.current as T)(...args);
      }, delay);
    },
    [delay],
  );
  return debouncedFn as T;
}
